import { useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox, DropdownButton, OnOffSwitch, OnOffSwitchSize, Tooltip } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/common-components/styles";
import { useOptedInIndustryTaxonomy } from "app/pages/sales-intelligence/modules/industry/hooks/useOptedInIndustryTaxonomy";

import { useFilterTitleByKey } from "../../hooks/useFilterTitleByKey";
import { useTeamsTrackingService } from "../../hooks/useTeamsTrackingService";
import { selectTeamsWithFilters, useTeamsListQuery } from "../../queries/useTeamsListQuery";
import { setSelectedTeamId, setTerritoryEnabled } from "../../store/action-creators";
import { selectSelectedTeamId, selectTerritoryEnabled } from "../../store/selectors";
import type { TeamListItem } from "../../types/teams.types";
import { buildTeamFiltersSummaryLines } from "../TeamFiltersTooltip/buildTeamFiltersSummaryLines";
import { TeamFiltersTooltipContent } from "../TeamFiltersTooltip/TeamFiltersTooltipContent";

export const TerritoryFiltersControl = () => {
    const { translate } = useTranslate();
    const dispatch = useDispatch();

    const tracking = useTeamsTrackingService();

    const { data: teamsWithFilters = [] } = useTeamsListQuery({ onlyMyTeams: true, select: selectTeamsWithFilters });

    const isEnabled = useSelector(selectTerritoryEnabled);
    const selectedTeamId = useSelector(selectSelectedTeamId);

    const titleByKey = useFilterTitleByKey();
    const industryTaxonomy = useOptedInIndustryTaxonomy();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    if (teamsWithFilters.length === 0) {
        return null;
    }

    const selectedTeam = teamsWithFilters.find((team) => team.teamId === selectedTeamId) ?? teamsWithFilters[0];
    const isSingleTeam = teamsWithFilters.length === 1;

    const teamName = (team: TeamListItem) => team.name ?? translate("si.lead_gen.territory_filters.unnamed_team");
    const teamLabel = (team: TeamListItem) =>
        translate("si.lead_gen.territory_filters.team_label", { name: teamName(team) });

    const handleToggle = () => {
        const nextEnabled = !isEnabled;
        dispatch(setTerritoryEnabled(nextEnabled));
        tracking.trackTerritoryFiltersToggled(nextEnabled);
    };

    const handleSelectTeam = (teamId: number) => {
        setIsDropdownOpen(false);
        dispatch(setSelectedTeamId(teamId));
        const team = teamsWithFilters.find((t) => t.teamId === teamId);
        tracking.trackTerritoryTeamChanged(team?.name ?? "unnamed");
    };

    const renderFiltersTooltip = (team: TeamListItem) => {
        const filterLines = buildTeamFiltersSummaryLines(team.filters, titleByKey, industryTaxonomy);

        if (filterLines.length === 0) {
            return null;
        }

        return (
            <Tooltip
                placement="top"
                content={
                    <TeamFiltersTooltipContent
                        lines={filterLines}
                        title={teamLabel(team)}
                        dataAutomation="territory-filters-tooltip"
                    />
                }
            >
                <Flex
                    inline
                    alignItems="center"
                    cursor="pointer"
                >
                    <Info24px
                        size="sm"
                        color={colorsPalettes.carbon["200"]}
                    />
                </Flex>
            </Tooltip>
        );
    };

    return (
        <Stack
            gap="8px"
            margin="8px 12px"
            padding="12px 16px"
            background={colorsPalettes.carbon[0]}
            border={`2px solid ${colorsPalettes.bluegrey[200]}`}
            borderRadius="8px"
            data-automation="territory-filters"
        >
            <Flex
                alignItems="center"
                gap="8px"
            >
                <OnOffSwitch
                    isSelected={isEnabled}
                    onClick={handleToggle}
                    size={OnOffSwitchSize.small}
                    data-automation="territory-filters-toggle"
                />

                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[500]}
                >
                    {translate("si.lead_gen.territory_filters.toggle.label")}
                </Text>

                {isSingleTeam ? renderFiltersTooltip(selectedTeam) : null}
            </Flex>

            {teamsWithFilters.length > 1 ? (
                <Combobox
                    placement="bottom-start"
                    isOpen={isDropdownOpen}
                    onOpenChange={setIsDropdownOpen}
                >
                    <Combobox.Trigger>
                        <StyledBaseDropdownButtonWrap buttonTextColor={colorsPalettes.carbon[500]}>
                            <DropdownButton
                                isOpen={isDropdownOpen}
                                hasValue
                                width="100%"
                                dataAutomation="territory-filters-team-select"
                            >
                                {teamLabel(selectedTeam)}
                            </DropdownButton>
                        </StyledBaseDropdownButtonWrap>
                    </Combobox.Trigger>

                    <Combobox.Content>
                        {teamsWithFilters.map((team) => (
                            <Combobox.Option
                                key={team.teamId}
                                id={String(team.teamId)}
                                textValue={teamLabel(team)}
                                isSelected={team.teamId === selectedTeam.teamId}
                                onClick={() => handleSelectTeam(team.teamId)}
                            >
                                <Flex
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap="8px"
                                    width="100%"
                                >
                                    <Combobox.OptionLabel>{teamLabel(team)}</Combobox.OptionLabel>
                                    {renderFiltersTooltip(team)}
                                </Flex>
                            </Combobox.Option>
                        ))}
                    </Combobox.Content>
                </Combobox>
            ) : null}
        </Stack>
    );
};
